import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { ImageWithFallback } from './figma/ImageWithFallback';
import { useLanguage } from '../contexts/LanguageContext';
import firstImage from '../assets/image/cherman4.jpeg';
import secondImage from '../assets/image/cherman5.jpeg';
import thirdImage from '../assets/image/cherman6.jpeg';
import fourthImage from '../assets/image/cherman7.jpeg';

export function Gallery() {
  const { t } = useLanguage();
  const [currentPage, setCurrentPage] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [windowWidth, setWindowWidth] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1200
  );

  const images = [
    { url: firstImage, alt: 'Temple Architecture', name: 'Temple Architecture' },
    { url: secondImage, alt: 'Meditation', name: 'Meditation' },
    { url: thirdImage, alt: 'Prayer', name: 'Prayer' },
    { url: fourthImage, alt: 'Spiritual Ceremony', name: 'Spiritual Ceremony' },
  ];
  // Responsive images per page
  const getImagesPerPage = () => {
    if (windowWidth < 640) return 1;
    if (windowWidth < 1024) return 2;
    return 4;
  };

  const imagesPerPage = getImagesPerPage();
  const totalPages = Math.ceil(images.length / imagesPerPage);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Reset if page exceeds total
  useEffect(() => {
    if (currentPage >= totalPages) {
      setCurrentPage(0);
    }
  }, [totalPages, currentPage]);

  const nextSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentPage((prev) => (prev + 1) % totalPages);
    setTimeout(() => setIsTransitioning(false), 600);
  };

  const prevSlide = () => {
    if (isTransitioning) return;
    setIsTransitioning(true);
    setCurrentPage((prev) => (prev - 1 + totalPages) % totalPages);
    setTimeout(() => setIsTransitioning(false), 600);
  };

  const goToPage = (page) => {
    if (isTransitioning || page === currentPage) return;
    setIsTransitioning(true);
    setCurrentPage(page);
    setTimeout(() => setIsTransitioning(false), 600);
  };

  const startIndex = currentPage * imagesPerPage;
  const visibleImages = images.slice(startIndex, startIndex + imagesPerPage);

  return (
    <section id="gallery" className="gallery">
      <div className="gallery__container">
        <div className="gallery__header">
          <h2 className="gallery__title">{t('gallery.title')}</h2>
          <p className="gallery__description">{t('gallery.description')}</p>
        </div>

        <div className="gallery__wrapper">
          {/* Previous Button */}
          {totalPages > 1 && (
            <button
              className="gallery__nav gallery__nav--prev"
              onClick={prevSlide}
              disabled={isTransitioning}
              aria-label="Previous images"
            >
              <ChevronLeft size={24} />
            </button>
          )}

          {/* Gallery Slider */}
          <div className="gallery__slider">
            <div key={currentPage} className="gallery__grid">
              {visibleImages.map((image, index) => (
                <div key={startIndex + index} className="gallery__item">
                  <ImageWithFallback src={image.url} alt={image.alt} />
                   <div className="gallery__item-name">{image.name}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Next Button */}
          {totalPages > 1 && (
            <button
              className="gallery__nav gallery__nav--next"
              onClick={nextSlide}
              disabled={isTransitioning}
              aria-label="Next images"
            >
              <ChevronRight size={24} />
            </button>
          )}
        </div>

        {/* Pagination Dots */}
        {totalPages > 1 && (
          <div className="gallery__dots">
            {Array.from({ length: totalPages }).map((_, index) => (
              <button
                key={index}
                className={`gallery__dot ${currentPage === index ? 'gallery__dot--active' : ''}`}
                onClick={() => goToPage(index)}
                disabled={isTransitioning}
                aria-label={`Go to page ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}